import React, { useEffect, useState } from 'react';
import { FaEdit, FaTrash, FaEye, FaPlus } from 'react-icons/fa';
import API_BASE_URL from '../config/api';

interface User {
  id: number;
  username?: string;
  password: string;
  pin?: string;
  role: 'client' | 'admin' | 'operator';
  first_name?: string;
  last_name?: string;
  email?: string;
  address?: string;
  contact?: string;
  status?: 'active' | 'inactive' | 'complete';
  spouse_detail?: string;
  dob?: string;
  evaluation_id?: number;
  created_at: string;
  assigned_to?: string;
  assigned_users?: string;
  active_status: number;
  assigned_status: number;
}

const defaultUser: Partial<User> = {
  username: '',
  password: '',
  pin: '',
  role: 'client',
  first_name: '',
  last_name: '',
  email: '',
  address: '',
  contact: '',
  status: 'active',
  spouse_detail: '',
  dob: '',
  evaluation_id: 0,
  created_at: '',
  assigned_to: '',
  assigned_users: '',
  active_status: 1,
  assigned_status: 0,
};

const Users: React.FC = () => {
  const [operators, setOperators] = useState<User[]>([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignUserId, setAssignUserId] = useState<number | null>(null);
  const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(null);

  // Fetch operators for assignment
  const fetchOperators = async () => {
    const res = await fetch(`${API_BASE_URL}/users`);
    const data = await res.json();
    setOperators(data.users.filter((u: User) => u.role === 'operator'));
  };

  // Show assign modal and fetch operators
  const handleAssignClick = (userId: number) => {
    setAssignUserId(userId);
    setShowAssignModal(true);
    fetchOperators();
  };

  // Assign operator to user
    const handleAssignOperator = async () => {
      if (!assignUserId || !selectedOperatorId) return;
      await fetch(`${API_BASE_URL}/users/${assignUserId}/assign-operator`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operatorId: selectedOperatorId }),
      });
      setShowAssignModal(false);
      setAssignUserId(null);
      setSelectedOperatorId(null);
      fetchUsers();
    };

  // Helper to get assigned operator name
  const getAssignedOperatorName = (operatorId: string | undefined) => {
    if (!operatorId) return '';
    const op = operators.find(o => o.id.toString() === operatorId);
    return op ? `${op.first_name || ''} ${op.last_name || ''}`.trim() : operatorId;
  };
  const [users, setUsers] = useState<User[]>([]);
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState(""); // Default value
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleAdminPasswordChange = async () => {
    setPasswordError("");
    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    setPasswordLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: modalUser.id,
          oldPassword,
          newPassword
        })
      });
      const data = await res.json();
      if (data.success) {
        setShowPasswordModal(false);
        setOldPassword("123456");
        setNewPassword("");
        setConfirmPassword("");
        alert("Password changed successfully!");
      } else {
        setPasswordError(data.message || "Failed to change password.");
      }
    } catch (err) {
      setPasswordError("Server error. Please try again.");
    }
    setPasswordLoading(false);
  };
  const [modalUser, setModalUser] = useState<Partial<User>>(defaultUser);
  const [modalMode, setModalMode] = useState<'create'|'edit'|'view'>('create');
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = async () => {
    const res = await fetch(`${API_BASE_URL}/users`);
    const data = await res.json();
    setUsers(data.users || []);
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    if (showModal && modalMode === 'create') {
      // Generate a 4-digit random PIN
      const randomPin = Math.floor(1000 + Math.random() * 9000).toString();
      setModalUser(prev => ({ ...prev, pin: randomPin }));
    }
    // Optionally, clear PIN on edit/view if needed
  }, [showModal, modalMode]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this user?')) return;
    await fetch(`${API_BASE_URL}/users/${id}`, { method: 'DELETE' });
    fetchUsers();
  };

  const handleEdit = (user: User) => {
    setModalUser(user);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleView = (user: User) => {
    setModalUser(user);
    setModalMode('view');
    setShowModal(true);
  };

  const handleCreate = () => {
    setModalUser(defaultUser);
    setModalMode('create');
    setShowModal(true);
  };

  const handleModalChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setModalUser({ ...modalUser, [e.target.name]: e.target.value });
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (modalMode === 'create') {
        await fetch(`${API_BASE_URL}/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(modalUser),
        });
      } else if (modalMode === 'edit' && modalUser.id) {
        await fetch(`${API_BASE_URL}/users/${modalUser.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(modalUser),
        });
      }
      setShowModal(false);
      fetchUsers();
    } finally {
      setSubmitting(false);
    }
  };

  const filteredUsers = users
    .filter(u => u.role === 'client')
    .filter(u =>
      (u.username ?? '').toLowerCase().includes(filter.toLowerCase()) ||
      (u.first_name ?? '').toLowerCase().includes(filter.toLowerCase()) ||
      (u.last_name ?? '').toLowerCase().includes(filter.toLowerCase()) ||
      (u.email ?? '').toLowerCase().includes(filter.toLowerCase())
    );

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <input
          className="border p-2 rounded w-1/3"
          placeholder="Filter by name, username, or email"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        <button className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2" onClick={handleCreate}>
          <FaPlus /> Create User
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded shadow">
          <thead>
            <tr>
              <th className="px-4 py-2">ID</th>
              <th className="px-4 py-2">Username</th>
              <th className="px-4 py-2">First Name</th>
              <th className="px-4 py-2">Last Name</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Assigned Status</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id} className="border-t">
                <td className="px-4 py-2">{user.id}</td>
                <td className="px-4 py-2">{user.username}</td>
                <td className="px-4 py-2">{user.first_name}</td>
                <td className="px-4 py-2">{user.last_name}</td>
                <td className="px-4 py-2">{user.email}</td>
                <td className="px-4 py-2">{user.role}</td>
                <td className="px-4 py-2">{user.status}</td>
                <td className="px-4 py-2">
                  {user.assigned_status === 1 ? (
                    <button
                      className="text-blue-600 underline cursor-pointer"
                      title="View Assigned Operator"
                      onClick={() => fetchOperators()}
                    >
                      {getAssignedOperatorName(user.assigned_to)}
                    </button>
                  ) : (
                    <button
                      className="text-red-600 underline cursor-pointer"
                      title="Assign Operator"
                      onClick={() => handleAssignClick(user.id)}
                    >
                      Unassigned
                    </button>
                  )}
                </td>
                <td className="px-4 py-2 flex gap-2">
                  <button className="text-blue-600" onClick={() => handleView(user)} title="View"><FaEye /></button>
                  <button className="text-green-600" onClick={() => handleEdit(user)} title="Edit"><FaEdit /></button>
                  <button className="text-red-600" onClick={() => handleDelete(user.id)} title="Delete"><FaTrash /></button>
                </td>
      {/* Assign Operator Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded text-left shadow-lg p-6 w-full max-w-md relative">
            <button className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-2xl font-bold" onClick={() => setShowAssignModal(false)}>&times;</button>
            <h2 className="text-xl font-bold mb-4">Assign Operator</h2>
            <div className="mb-4">
              <label className="block font-semibold mb-2">Select Operator</label>
              <select
                className="border p-2 rounded w-full"
                value={selectedOperatorId || ''}
                onChange={e => setSelectedOperatorId(Number(e.target.value))}
              >
                <option value="">Select Operator</option>
                {operators.map(op => (
                  <option key={op.id} value={op.id}>{op.first_name} {op.last_name} ({op.email})</option>
                ))}
              </select>
            </div>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded mt-2"
              onClick={handleAssignOperator}
              disabled={!selectedOperatorId}
            >
              Assign
            </button>
          </div>
        </div>
      )}
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr><td colSpan={8} className="text-center py-4">No users found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Modal for Create/Edit/View */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded text-left shadow-lg p-6 w-full max-w-md relative">
            <button className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-2xl font-bold" onClick={() => setShowModal(false)}>&times;</button>
            <h2 className="text-xl font-bold mb-4">{modalMode === 'create' ? 'Create User' : modalMode === 'edit' ? 'Edit User' : 'View User'}</h2>
            <form onSubmit={handleModalSubmit}>
              <div className="mb-2">
              {modalMode === 'view' && (
                <div className="mb-4 flex justify-end">
                  <button
                    className="bg-blue-600 text-white px-4 py-2 rounded font-semibold"
                    type="button"
                    onClick={() => {
                      setOldPassword(modalUser.password || "");
                      setShowPasswordModal(true);
                    }}
                  >
                    Change Password
                  </button>
                </div>
              )}
                <label className="block font-semibold">Username</label>
                <input className="border p-2 rounded w-full" name="username" value={modalUser.username || ''} onChange={handleModalChange} disabled={modalMode === 'view'} required />
              </div>
              <div className="mb-2">
                <label className="block font-semibold">First Name</label>
                <input className="border p-2 rounded w-full" name="first_name" value={modalUser.first_name || ''} onChange={handleModalChange} disabled={modalMode === 'view'} required />
              </div>
              <div className="mb-2">
                <label className="block font-semibold">Last Name</label>
                <input className="border p-2 rounded w-full" name="last_name" value={modalUser.last_name || ''} onChange={handleModalChange} disabled={modalMode === 'view'} required />
              </div>
              <div className="mb-2">
                <label className="block font-semibold">Email</label>
                <input className="border p-2 rounded w-full" name="email" value={modalUser.email || ''} onChange={handleModalChange} disabled={modalMode === 'view'} required />
              </div>
              <div className="mb-2">
                <label className="block font-semibold">Role</label>
                <select className="border p-2 rounded w-full" name="role" value={modalUser.role || 'client'} onChange={handleModalChange} disabled={modalMode === 'view'} required>
                  <option value="admin">Admin</option>
                  <option value="operator">Operator</option>
                  <option value="client">Client</option>
                </select>
              </div>
              <div className="mb-2">
                <label className="block font-semibold">Status</label>
                <input className="border p-2 rounded w-full" name="status" value={modalUser.status || ''} onChange={handleModalChange} disabled={modalMode === 'view'} />
              </div>
              <div className="mb-2">
                <label className="block mb-2 font-semibold text-gray-700">User PIN (4-digit)</label>
                <input
                  type="text"
                  maxLength={4}
                  name="pin"
                  value={modalUser.pin || ''}
                  onChange={handleModalChange}
                  className="border p-2 rounded w-full text-center text-xl tracking-widest"
                  readOnly={modalMode === 'view'}
                  required
                />
              </div>
              <div className="mb-2">
                <label className="block font-semibold">Password</label>
                <input
                  className="border p-2 rounded w-full"
                  name="password"
                  type="password"
                  value={modalUser.password || ''}
                  onChange={handleModalChange}
                  disabled={modalMode === 'view'}
                  required={modalMode === 'create'}
                />
              </div>
              
              {modalMode !== 'view' && (
                <button
                  className={`bg-blue-600 text-white px-4 py-2 rounded mt-4 ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? 'Processing...' : (modalMode === 'create' ? 'Create' : 'Update')}
                </button>
              )}
            </form>
          </div>
        </div>
      )}
      {/* Modal for Change User Password (admin) */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded text-left shadow-lg p-6 w-full max-w-md relative">
            <button className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-2xl font-bold" onClick={() => setShowPasswordModal(false)}>&times;</button>
            <h2 className="text-xl font-bold mb-4">Change User Password</h2>
            <form onSubmit={handleAdminPasswordChange}>
              {/* <div className="mb-2">
                <label className="block font-semibold">Bcrypt Hashed Password</label>
                <input
                  type="text"
                  className="border p-2 rounded w-full bg-gray-100 font-mono text-xs"
                  value={modalUser.password || ""}
                  readOnly
                />
              </div> */}
              <div className="mb-2">
                <label className="block font-semibold">Old Password</label>
                <input
                  type="password"
                  className="border p-2 rounded w-full"
                  value={oldPassword}
                  onChange={e => setOldPassword(e.target.value)}
                  minLength={6}
                  readOnly={modalMode === 'view'}
                  required
                />
              </div>
              <div className="mb-2">
                <label className="block font-semibold">New Password</label>
                <input
                  type="password"
                  className="border p-2 rounded w-full"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  minLength={6}
                  required
                />
              </div>
              <div className="mb-2">
                <label className="block font-semibold">Confirm Password</label>
                <input
                  type="password"
                  className="border p-2 rounded w-full"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  minLength={6}
                  required
                />
              </div>
              {passwordError && (
                <div className="text-red-600 mb-2">{passwordError}</div>
              )}
              <button
                className={`bg-blue-600 text-white px-4 py-2 rounded w-full font-semibold ${passwordLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={handleAdminPasswordChange}
                disabled={passwordLoading}
              >
                {passwordLoading ? 'Saving...' : 'Save Password'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
